import { useMemo, useCallback } from "react";
import { normalizeValue, selectionToUserEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelectedChips,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";
import { useUserPool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type UserSelectorProps = Omittable<{
  /** Currently selected user ID(s) */
  value?: string | string[] | null;
  /** Called when selection changes with an array of user IDs */
  onValueChange?: (ids: string[]) => void;
  /** User IDs to hide from the pool */
  exclude?: string[];
  /** Restrict the pool to active members only (excludes invited/pending). */
  activeOnly?: boolean;
  /** Maximum number of selections (1 for single-select) */
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Render selections as removable chips in the trigger. */
  chips?: boolean;
  /** Leading icon shown in the trigger. */
  icon?: IconName;
  /** Stretch trigger to fill its container. */
  fill?: boolean;
  /** Match popover width to trigger. */
  contentFit?: boolean;
  /** Popover min-width preset. */
  contentSize?: EntitySelectorContentSize;
  /** Controlled open state. */
  open?: boolean;
  defaultOpen?: boolean;
  /** Called when the selector opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Override the default trigger + content. Receives EntitySelector context. */
  children?: React.ReactNode;
}>;

/**
 * Domain wrapper around EntitySelector for selecting workspace users.
 * Handles data fetching, item mapping, and value conversion internally.
 */
export function UserSelector({
  value,
  onValueChange,
  exclude,
  activeOnly,
  maxSelections,
  variant,
  placeholder = "Select members...",
  size,
  disabled,
  chips,
  icon,
  fill,
  contentFit,
  contentSize,
  open,
  defaultOpen,
  onOpenChange,
  children,
}: UserSelectorProps) {
  const { pool, loading } = useUserPool({ exclude, activeOnly });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
      noItemsMessage: "No members yet",
    }),
    [pool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return ids
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <EntitySelector
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children ?? (
        <>
          {chips ? (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips
              fill={fill}
              icon={icon}
            />
          ) : (
            <SelectionEntityTrigger
              toEntity={selectionToUserEntity}
              placeholder={placeholder}
              title="Members"
              emptyIcon={icon}
              size={size}
              disabled={disabled}
              fill={fill}
            />
          )}
          <EntitySelectorContent size={contentSize} fit={contentFit}>
            <EntitySelectorInput />
            <EntitySelectorSelectedChips label="Selected" />
            <EntitySelectorPool value={pool.id} />
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
